import { createFileRoute } from "@tanstack/react-router";
import { BreadcrumbHeader } from "@/components/primitives/breadcrumb-header";
import {
  Article,
  ArticleBody,
  ArticleContainer,
  ArticleDescription,
  ArticleHeader,
  ArticleHeading,
  ArticleHighlightImage,
  ArticleList,
  ArticleListItem,
  ArticleOrderedList,
  ArticleOrderedListItem,
  ArticleParagraph,
  ArticleRelatedLink,
  ArticleSection,
  ArticleSectionHeader,
} from "@/components/primitives/article";
import { PageContainer } from "@/components/primitives/layout";
import { SingleBreadcrumb } from "@/components/primitives/single-breadcrumb";

const TITLE = "Why use an ice cream calculator?";
const DESCRIPTION = "Learn how a calculator can help improve your recipes.";

export const Route = createFileRoute("/learn/why-use-an-ice-cream-calculator")({
  head: () => ({
    meta: [
      { title: `${TITLE} - Scoopulator` },
      { name: "description", content: DESCRIPTION },
    ],
  }),
  component: WhyUseCalculatorPage,
});

function WhyUseCalculatorPage() {
  return (
    <PageContainer>
      <ArticleContainer>
        <BreadcrumbHeader>
          <SingleBreadcrumb href="/learn" label="Learn more about ice cream" />
        </BreadcrumbHeader>

        <Article>
          <ArticleHeader>
            <ArticleHeading>{TITLE}</ArticleHeading>
            <ArticleDescription>{DESCRIPTION}</ArticleDescription>
          </ArticleHeader>
          <ArticleHighlightImage
            src="/articles/why.webp"
            alt="Why use an ice cream calculator infographic"
            width={808}
            height={360}
          />
          <ArticleBody>
            <ArticleSection>
              <ArticleParagraph>
                Making ice cream is simple, until it isn&apos;t.
              </ArticleParagraph>
              <ArticleParagraph>
                When you&apos;re starting off, it&apos;s easy to follow a proven
                recipe and make something delicious. But as you start
                experimenting with new ingredients, flavors, and techniques, you
                may run into some common issues:
              </ArticleParagraph>
              <ArticleList>
                <ArticleListItem>
                  The ice cream is too hard (or too soft) out of the freezer
                </ArticleListItem>
                <ArticleListItem>
                  The ice cream is too sweet (or not sweet enough)
                </ArticleListItem>
                <ArticleListItem>The ice cream is too icy</ArticleListItem>
                <ArticleListItem>
                  The ice cream melts quickly and has a thin texture
                </ArticleListItem>
              </ArticleList>
              <ArticleParagraph>
                In order to solve these problems, ice cream science can help us.
                As it turns out, there are mathematical relationships between
                the ingredients you use and the final texture and sweetness of
                your ice cream.
              </ArticleParagraph>
              <ArticleParagraph>
                Most of the time, great ice cream comes down to two main
                factors: how soft your ice cream is out of the freezer, and how
                sweet it tastes.
              </ArticleParagraph>
            </ArticleSection>

            <ArticleSection>
              <ArticleSectionHeader id="hardness">
                Predicting ice cream hardness
              </ArticleSectionHeader>
              <ArticleParagraph>
                This is the most critical role of an ice cream calculator. By
                predicting how hard your ice cream will be out of the freezer,
                you can ensure that it is perfectly scoopable and will have the
                correct texture.
              </ArticleParagraph>
              <ArticleParagraph>
                The way calculators do this (primarily) is by tracking:
              </ArticleParagraph>
              <ArticleOrderedList>
                <ArticleOrderedListItem>Water content</ArticleOrderedListItem>
                <ArticleOrderedListItem>
                  Freezing point depression factor (FPDF) of ingredients
                </ArticleOrderedListItem>
              </ArticleOrderedList>
              <ArticleParagraph>
                The more water you have in your recipe, the more likely it will
                be to form large ice crystals and become hard. The way to combat
                this is by adding ingredients which lower the freezing point,
                such as sugar or alcohol. We quantify this effect as FPDF, which
                is expressed as a percentage in relation to sucrose. The higher
                the value, the more powerful the anti-freezing properties.
              </ArticleParagraph>
              <ArticleList>
                <ArticleListItem>
                  <strong>Low FPDF:</strong> Too little freezing point
                  depression results in more ice formation, leading to a
                  rock-hard texture that is difficult to scoop.
                </ArticleListItem>
                <ArticleListItem>
                  <strong>High FPDF:</strong> Excessive depression prevents
                  enough ice from forming, resulting in a soft,
                  &ldquo;soupy&rdquo; consistency.
                </ArticleListItem>
              </ArticleList>
              <ArticleParagraph>
                Ingredients often vary in their FPDF. Take, for example,
                dextrose vs sucrose. Dextrose has 190% the FPDF of sucrose,
                which is why it is so commonly used in ice cream recipes. By
                using a calculator, you can easily swap out sugars and other
                ingredients while maintaining the same FPDF, ensuring that your
                ice cream remains perfectly scoopable.
              </ArticleParagraph>
              <ArticleRelatedLink
                href="/learn/fpdf"
                title="How to calculate FPDF (PAC)"
                description="Learn the science behind making soft, scoopable ice cream."
              />
            </ArticleSection>

            <ArticleSection>
              <ArticleSectionHeader id="sweetness">
                Estimating sweetness
              </ArticleSectionHeader>
              <ArticleParagraph>
                At first glance, sweetness may seem a simple matter of adding
                sugar to taste. However, as discussed above, sugars are the
                primary lever for controlling ice cream hardness. This means
                that you can&apos;t adjust sugar on the fly without negatively
                impacting the texture.
              </ArticleParagraph>
              <ArticleParagraph>
                And just like with FPDF, different sweeteners have differing
                levels of sweetness. This is quantified as{" "}
                <strong>Relative Sweetness</strong>, which is expressed as a
                percentage compared to sucrose. Dextrose, for example, is only
                70% as sweet as sucrose. This is another reason why it is so
                commonly used in premium ice cream recipes; it allows for the
                creation of scoopable ice cream that isn&apos;t overly sweet.
              </ArticleParagraph>
              <ArticleParagraph>
                By using an ice cream calculator, you can estimate the sweetness
                ahead of time and prevent last-minute adjustments that could
                ruin the recipe.
              </ArticleParagraph>
            </ArticleSection>

            <ArticleSection>
              <ArticleSectionHeader id="other-factors">
                Other factors
              </ArticleSectionHeader>
              <ArticleParagraph>
                Targeting the correct hardness and sweetness will get you most
                of the way to a great ice cream, but there are a few other
                factors as well that a calculator will help you manage:
              </ArticleParagraph>
              <ArticleList>
                <ArticleListItem>
                  <strong>Total solids:</strong> Too little solids results in a
                  thin, icy texture that melts quickly. Too many solids may lead
                  to a gritty texture.
                </ArticleListItem>
                <ArticleListItem>
                  <strong>Fat content:</strong> Targeting a specific fat
                  percentage ensures your ice cream has the perfect balance of
                  creamy mouthfeel and flavor release without becoming
                  unpleasantly greasy.
                </ArticleListItem>
                <ArticleListItem>
                  <strong>Milk solids not fat (MSNF):</strong> The primary
                  source of lactose and proteins. MSNF contributes to the body
                  of the ice cream, but too much can lead to a grainy texture.
                </ArticleListItem>
                <ArticleListItem>
                  <strong>Stabilizers:</strong> These help maintain the texture
                  and prevent ice crystal formation, but if used in excess will
                  result in a gummy or slimy mouthfeel.
                </ArticleListItem>
                <ArticleListItem>
                  <strong>Emulsifiers:</strong> Egg yolks and other emulsifiers
                  improve texture by making it easier to whip air into the ice
                  cream.
                </ArticleListItem>
              </ArticleList>
            </ArticleSection>

            <ArticleSection>
              <ArticleSectionHeader id="caution">
                A note of caution: calculators are not gospel
              </ArticleSectionHeader>
              <ArticleParagraph>
                Calculators are useful tools, but they aren&apos;t infallible.
                The chemistry and physics of ice cream have many complex
                interactions which cannot be perfectly modeled (despite our best
                efforts!). This means that the numbers you get from a calculator
                are best used as a starting point, not an exact prediction. Use
                the numbers to get into the right ballpark, then iterate: taste
                the base, adjust, churn, and take notes. When the calculator and
                your taste buds disagree, trust your intuition.
              </ArticleParagraph>
            </ArticleSection>

            <ArticleSection>
              <ArticleSectionHeader id="summary">Summary</ArticleSectionHeader>
              <ArticleParagraph>
                For those who like to experiment with different ingredients and
                want to move beyond basic recipes, understanding how those
                ingredients interact is essential. And unless you like doing the
                math by hand, calculators are the best way to apply this
                knowledge in a practical way.
              </ArticleParagraph>
              <ArticleParagraph>
                If you haven&apos;t tried using an ice cream calculator before,
                I highly recommend giving it a shot. Spend less time
                troubleshooting, and more time enjoying delicious ice cream.
              </ArticleParagraph>
              <ArticleRelatedLink
                className="mt-8"
                href="/calc"
                title="Use Scoopulator's free ice cream calculator"
                label="Try it out"
                srLabel="Ice cream calculator"
                description="Build a recipe, hit your targets, and iterate with confidence."
              />
            </ArticleSection>
          </ArticleBody>
        </Article>
      </ArticleContainer>
    </PageContainer>
  );
}
